import { isBiGraphSource, withholdsUnboundBiInterpretations } from '../../../../supabase/functions/_shared/bi-v2/retrievalContext';
import { isMemoryArchiveType } from "@/lib/leaderMemoryVisibility";
import type {
  CoachSessionPrepData,
  LeaderLedgerEventItem,
  PracticeSummaryItem,
  ReflectionItem,
  TranscriptItem,
} from "@/hooks/useCoachSessionPrepData";
import type { CoachNote } from "@/hooks/useCoachNotes";
import type { CarryForwardSourceTable } from "@/types/carryForward";
import type { ObservationClusterV2, ObservationV2 } from "../../../../supabase/functions/_shared/bi-v2/contracts";

export type LeadershipRecordVisibility =
  | "coach_only"
  | "coach_reviewed"
  | "leader_safe"
  | "not_shareable_yet";

export type LeadershipRecordSourceType =
  | "bi_observation"
  | "context_graph_node"
  | "leader_memory_event"
  | "practice_summary"
  | "transcript_evidence"
  | "reflection"
  | "coach_note"
  | "shared_summary";

export type LeadershipRecordConfidence = "high" | "medium" | "low";

export type LeadershipRecordItem = {
  id: string;
  leaderId?: string;
  sourceType: LeadershipRecordSourceType;
  sourceId: string;
  title: string;
  observedFact: string;
  occurredAt?: string;
  createdAt: string;
  confidence?: LeadershipRecordConfidence;
  visibility: LeadershipRecordVisibility;
  evidenceCount?: number;
  evidenceSources?: string[];
  whySurfaced?: string;
  relatedNodeIds?: string[];
  relatedMomentIds?: string[];
  uncertainty?: string[];
  carryForwardSourceTable?: CarryForwardSourceTable;
  carryForwardSourceRowId?: string;
};

export function isLeaderVisibleRecordItem(item: Partial<Pick<LeadershipRecordItem, "visibility">>): boolean {
  return item.visibility === "leader_safe";
}

type ContextGraphNode = NonNullable<CoachSessionPrepData["canonicalBehavior"]>["contextGraph"]["recentNodes"][number];
type ContextGraphEdge = NonNullable<CoachSessionPrepData["canonicalBehavior"]>["contextGraphEdges"][number];

const DEFAULT_VISIBILITY: LeadershipRecordVisibility = "coach_only";

function compact<T>(values: Array<T | null | undefined>): T[] {
  return values.filter((value): value is T => value !== null && value !== undefined);
}

function firstText(values: Array<string | null | undefined>): string | null {
  for (const value of values) {
    const normalized = value?.replace(/\s+/g, " ").trim();
    if (normalized) return normalized;
  }
  return null;
}

function humanizeToken(value: string): string {
  return value
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/^./, (letter) => letter.toUpperCase());
}

function normalizeConfidence(value: string | null | undefined): LeadershipRecordConfidence | undefined {
  if (value === "high" || value === "medium" || value === "low") return value;
  return undefined;
}

function metadataText(metadata: Record<string, unknown> | null | undefined, keys: string[]): string | null {
  if (!metadata) return null;
  for (const key of keys) {
    const value = metadata[key];
    if (typeof value === "string" && value.trim()) return value.trim();
  }
  return null;
}

function metadataStringList(metadata: Record<string, unknown> | null | undefined, keys: string[]): string[] | undefined {
  if (!metadata) return undefined;
  for (const key of keys) {
    const value = metadata[key];
    if (Array.isArray(value)) {
      const list = value.filter((entry): entry is string => typeof entry === "string" && entry.trim().length > 0);
      if (list.length > 0) return list;
    }
  }
  return undefined;
}

function evidenceSourceList(values: Array<string | null | undefined>): string[] | undefined {
  const sources = compact(values.map((value) => {
    const normalized = value?.replace(/\s+/g, " ").trim();
    return normalized || undefined;
  }));
  return sources.length > 0 ? Array.from(new Set(sources)) : undefined;
}

function buildTranscriptItem(leaderId: string, transcript: TranscriptItem): LeadershipRecordItem | null {
  const observedFact = firstText([
    transcript.summary,
    transcript.analysis_status === "analyzed" ? "Transcript evidence is available for coach review." : null,
  ]);
  if (!observedFact) return null;

  return {
    id: `transcript_evidence:${transcript.id}`,
    leaderId,
    sourceType: "transcript_evidence",
    sourceId: transcript.id,
    title: transcript.title || "Transcript evidence",
    observedFact,
    occurredAt: transcript.created_at,
    createdAt: transcript.created_at,
    confidence: transcript.coach_verified ? "high" : transcript.analysis_status === "analyzed" ? "medium" : "low",
    visibility: DEFAULT_VISIBILITY,
    evidenceCount: 1,
    evidenceSources: evidenceSourceList([transcript.title ? `Transcript: ${transcript.title}` : "Transcript evidence"]),
    whySurfaced: "Transcript evidence is available for coach review.",
    relatedMomentIds: compact([transcript.meeting_id || undefined]),
    uncertainty: transcript.coach_verified
      ? undefined
      : ["This transcript evidence has not been marked coach-verified yet."],
  };
}

function buildPracticeSummaryItem(leaderId: string, summary: PracticeSummaryItem): LeadershipRecordItem | null {
  const observedFact = firstText([
    summary.transcript_summary,
    summary.leader_reflection_text,
    summary.behavior_insights,
  ]);
  if (!observedFact) return null;

  return {
    id: `practice_summary:${summary.id}`,
    leaderId,
    sourceType: "practice_summary",
    sourceId: summary.id,
    title: summary.meeting_title || "Practice summary",
    observedFact,
    occurredAt: summary.meeting_start_time,
    createdAt: summary.created_at,
    confidence: "medium",
    visibility: DEFAULT_VISIBILITY,
    evidenceCount: compact([summary.transcript_summary, summary.leader_reflection_text, summary.behavior_insights]).length,
    evidenceSources: evidenceSourceList([
      summary.meeting_title ? `Practice summary: ${summary.meeting_title}` : "Practice summary",
      summary.transcript_upload_id ? `Transcript: ${summary.transcript_upload_id}` : undefined,
      summary.meeting_id ? `Meeting: ${summary.meeting_id}` : undefined,
    ]),
    whySurfaced: "Practice summary combines transcript evidence and leader reflection when available.",
    relatedMomentIds: compact([summary.meeting_id, summary.transcript_upload_id || undefined]),
    uncertainty: summary.leader_reflection_text
      ? undefined
      : ["No direct leader reflection is attached to this practice summary."],
  };
}

function buildReflectionItem(leaderId: string, reflection: ReflectionItem): LeadershipRecordItem | null {
  const observedFact = firstText([reflection.text]);
  if (!observedFact || !reflection.created_at) return null;

  return {
    id: `reflection:${reflection.id}`,
    leaderId,
    sourceType: reflection.source === "shared_reflection" ? "shared_summary" : "reflection",
    sourceId: reflection.id,
    title: reflection.title || "Reflection",
    observedFact,
    occurredAt: reflection.created_at,
    createdAt: reflection.created_at,
    confidence: "medium",
    visibility: DEFAULT_VISIBILITY,
    evidenceCount: 1,
    evidenceSources: evidenceSourceList([reflection.title ? `Reflection: ${reflection.title}` : `Reflection: ${reflection.id}`]),
    whySurfaced: reflection.source === "shared_reflection"
      ? "Shared reflection is available for coach review."
      : "Leader reflection is available for coach review.",
    relatedMomentIds: compact([reflection.meeting_id || undefined]),
  };
}

function buildLedgerItem(leaderId: string, event: LeaderLedgerEventItem): LeadershipRecordItem | null {
  if (isMemoryArchiveType(event.source_type)) return null;
  const title = firstText([
    metadataText(event.metadata, ["title", "label"]),
    humanizeToken(event.event_type),
  ]);
  const observedFact = firstText([
    metadataText(event.metadata, ["observedFact", "observed_fact", "summary", "description"]),
    `${humanizeToken(event.event_type)} was recorded from ${humanizeToken(event.source_type)} evidence.`,
  ]);
  if (!title || !observedFact) return null;

  return {
    id: `leader_memory_event:${event.id}`,
    leaderId,
    sourceType: "leader_memory_event",
    sourceId: event.id,
    title,
    observedFact,
    occurredAt: event.created_at,
    createdAt: event.created_at,
    confidence: normalizeConfidence(event.confidence_level),
    visibility: DEFAULT_VISIBILITY,
    evidenceCount: 1,
    evidenceSources: evidenceSourceList([
      `${humanizeToken(event.source_type)}: ${event.id}`,
    ]),
    whySurfaced: `Leader memory event from ${humanizeToken(event.source_type)} evidence.`,
    relatedNodeIds: metadataStringList(event.metadata, ["relatedNodeIds", "related_node_ids"]),
    relatedMomentIds: metadataStringList(event.metadata, ["relatedMomentIds", "related_moment_ids"]),
  };
}

function relatedNodeIdsForNode(node: ContextGraphNode, edges: ContextGraphEdge[]): string[] {
  const related = new Set<string>([node.id]);
  edges.forEach((edge) => {
    if (edge.from_node_id === node.id) related.add(edge.to_node_id);
    if (edge.to_node_id === node.id) related.add(edge.from_node_id);
  });
  return [...related];
}

function buildContextGraphItem(
  leaderId: string,
  node: ContextGraphNode,
  edges: ContextGraphEdge[],
): LeadershipRecordItem | null {
  if (isMemoryArchiveType(node.node_type) || isMemoryArchiveType(node.source_type)) return null;
  const title = firstText([node.title, humanizeToken(node.node_type)]);
  if (!title) return null;

  return {
    id: `context_graph_node:${node.id}`,
    leaderId,
    sourceType: "context_graph_node",
    sourceId: node.id,
    title,
    observedFact: `Connected evidence node captured: ${title}.`,
    occurredAt: node.created_at,
    createdAt: node.created_at,
    confidence: "medium",
    visibility: DEFAULT_VISIBILITY,
    evidenceCount: 1,
    evidenceSources: evidenceSourceList([
      `${humanizeToken(node.source_type)}: ${node.source_id || node.id}`,
    ]),
    whySurfaced: "Context graph node is connected to existing evidence.",
    relatedNodeIds: relatedNodeIdsForNode(node, edges),
    relatedMomentIds: compact([node.source_id || undefined]),
    uncertainty: ["This node should be read with its source evidence before it is carried forward."],
  };
}

function buildBiObservationItem(leaderId: string, observation: ObservationV2): LeadershipRecordItem | null {
  const title = firstText([observation.behavior_theme]);
  if (!title) return null;

  const observedFact = firstText([
    metadataText(observation.metadata, ["observedFact", "observed_fact", "summary", "description", "snippet"]),
    `Evidence observed around ${title}.`,
  ]);
  if (!observedFact) return null;

  return {
    id: `bi_observation:${observation.id}`,
    leaderId,
    sourceType: "bi_observation",
    sourceId: observation.id,
    title,
    observedFact,
    occurredAt: observation.updated_at || observation.created_at,
    createdAt: observation.created_at,
    confidence: normalizeConfidence(observation.confidence_level) || "low",
    visibility: DEFAULT_VISIBILITY,
    evidenceCount: 1,
    evidenceSources: evidenceSourceList([
      `${observation.source_type}: ${observation.source_artifact_id || observation.id}`,
      observation.meeting_id ? `meeting: ${observation.meeting_id}` : undefined,
    ]),
    whySurfaced: `Behavioral intelligence observation from ${observation.evidence_type} evidence.`,
    relatedNodeIds: metadataStringList(observation.metadata, ["relatedNodeIds", "related_node_ids"]),
    relatedMomentIds: compact([observation.meeting_id || undefined, observation.source_artifact_id || undefined]),
    uncertainty: observation.contradicts_observation_id
      ? ["A related observation points to mixed evidence and should be reviewed by the coach."]
      : undefined,
  };
}

function buildBiClusterItem(leaderId: string, cluster: ObservationClusterV2): LeadershipRecordItem | null {
  const title = firstText([cluster.behavior_theme]);
  if (!title) return null;

  const uncertainty = compact([
    cluster.contradiction_count > 0
      ? "Contradictory or mixed evidence exists and should be reviewed by the coach."
      : undefined,
    cluster.supporting_meeting_count < 2
      ? "Available support may be limited to one recent meeting."
      : undefined,
  ]);

  return {
    id: `bi_observation:${cluster.id}`,
    leaderId,
    sourceType: "bi_observation",
    sourceId: cluster.id,
    title,
    observedFact: `Evidence grouped around ${title}.`,
    occurredAt: cluster.updated_at || cluster.created_at,
    createdAt: cluster.created_at,
    confidence: normalizeConfidence(cluster.confidence_level) || "low",
    visibility: DEFAULT_VISIBILITY,
    evidenceCount: Math.max(1, cluster.supporting_artifact_count + cluster.supporting_meeting_count),
    evidenceSources: evidenceSourceList([
      `${cluster.primary_evidence_type} evidence cluster: ${cluster.id}`,
      cluster.meeting_id ? `meeting: ${cluster.meeting_id}` : undefined,
    ]),
    whySurfaced: "Behavioral intelligence cluster groups related evidence for coach review.",
    relatedMomentIds: compact([cluster.meeting_id || undefined]),
    uncertainty: uncertainty.length > 0 ? uncertainty : undefined,
  };
}

function buildCoachNoteItem(leaderId: string, note: CoachNote): LeadershipRecordItem | null {
  const observedFact = firstText([note.content]);
  if (!observedFact) return null;

  return {
    id: `coach_note:${note.id}`,
    leaderId,
    sourceType: "coach_note",
    sourceId: note.id,
    title: note.pinned ? "Pinned coach note" : "Coach note",
    observedFact,
    occurredAt: note.updated_at || note.created_at,
    createdAt: note.created_at,
    confidence: "medium",
    visibility: DEFAULT_VISIBILITY,
    evidenceCount: 1,
    evidenceSources: evidenceSourceList([`Coach note: ${note.id}`]),
    whySurfaced: note.pinned ? "Pinned coach note is available for carry-forward review." : "Coach note is available for review.",
    uncertainty: ["Coach notes remain coach-only unless explicitly reviewed for sharing."],
  };
}

function sortRecordItems(items: LeadershipRecordItem[]): LeadershipRecordItem[] {
  return [...items].sort((a, b) => {
    const bTime = Date.parse(b.createdAt || b.occurredAt || "");
    const aTime = Date.parse(a.createdAt || a.occurredAt || "");
    return bTime - aTime || a.id.localeCompare(b.id);
  });
}

export function buildLeadershipRecordItems(params: {
  leaderId: string;
  prepData: CoachSessionPrepData | null;
  notes?: CoachNote[];
  limit?: number;
}): LeadershipRecordItem[] {
  const notes = params.notes || [];
  const original = params.prepData;
  const suppress = original?.frameworkContextStatus === 'unavailable'
    || Boolean(original?.canonicalBehavior?.governedBi && withholdsUnboundBiInterpretations(original.canonicalBehavior.governedBi));
  // All Record callers share the same interpretation boundary. Preserve source
  // identities and authored records; old generated prose is not observed fact.
  const authoredNodes = (original?.canonicalBehavior?.contextGraph?.recentNodes || [])
    .filter(node => ["coach_note", "leader_chat_message", "leader_artifact"].includes(node.source_type)
      && node.metadata?.message_kind !== "assistant_response");
  const authoredNodeIds = new Set(authoredNodes.map(node => node.id));
  const prepData = original && suppress ? {
    ...original,
    practiceSummaries: [],
    transcripts: (original.transcripts || []).map(row => ({ ...row, summary: "Transcript source is available for review." })),
    ledgerEvents: (original.ledgerEvents || []).filter(event => event.source_type !== "leader_chat_response"
      && event.metadata?.message_kind !== "assistant_response" && !isBiGraphSource(event.source_type)),
    canonicalBehavior: original.canonicalBehavior ? {
      ...original.canonicalBehavior,
      biObservations: [],
      biClusters: [],
      contextGraph: { ...original.canonicalBehavior.contextGraph, recentNodes: authoredNodes },
      contextGraphEdges: (original.canonicalBehavior.contextGraphEdges || [])
        .filter(edge => authoredNodeIds.has(edge.from_node_id) && authoredNodeIds.has(edge.to_node_id)),
    } : undefined,
  } : original;
  const archivedNodeIds = new Set((prepData?.canonicalBehavior?.contextGraph?.recentNodes || [])
    .filter(node => isMemoryArchiveType(node.node_type) || isMemoryArchiveType(node.source_type)).map(node => node.id));
  const contextGraphEdges = (prepData?.canonicalBehavior?.contextGraphEdges || [])
    .filter(edge => !archivedNodeIds.has(edge.from_node_id) && !archivedNodeIds.has(edge.to_node_id));
  const items = compact([
    ...notes.map((note) => buildCoachNoteItem(params.leaderId, note)),
    ...((prepData?.ledgerEvents || []).map((event) => buildLedgerItem(params.leaderId, event))),
    ...((prepData?.canonicalBehavior?.biObservations || []).map((observation) =>
      buildBiObservationItem(params.leaderId, observation)
    )),
    ...((prepData?.canonicalBehavior?.contextGraph?.recentNodes || []).map((node) =>
      buildContextGraphItem(params.leaderId, node, contextGraphEdges)
    )),
    ...((prepData?.canonicalBehavior?.biClusters || []).map((cluster) => buildBiClusterItem(params.leaderId, cluster))),
    ...((prepData?.practiceSummaries || []).map((summary) => buildPracticeSummaryItem(params.leaderId, summary))),
    ...((prepData?.reflections || []).map((reflection) => buildReflectionItem(params.leaderId, reflection))),
    ...((prepData?.transcripts || []).map((transcript) => buildTranscriptItem(params.leaderId, transcript))),
  ]);

  return sortRecordItems(items).slice(0, params.limit ?? items.length);
}
